import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  EyeIcon, 
  BuildingOffice2Icon,
  UsersIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { organizationAPI, departmentsAPI as departmentAPI, employeeAPI } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';

const DepartmentManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCompany, setSelectedCompany] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [companiesResponse, departmentsResponse, employeesResponse] = await Promise.all([
        organizationAPI.getCompanies(),
        departmentAPI.getAll(),
        employeeAPI.getAll({ employment_status: 'active' })
      ]);

      if (companiesResponse.data.success) {
        setCompanies(companiesResponse.data.data);
        if (companiesResponse.data.data.length > 0 && !selectedCompany) {
          setSelectedCompany(companiesResponse.data.data[0].id);
        }
      }

      if (departmentsResponse.data.departments) {
        setDepartments(departmentsResponse.data.departments);
      }

      if (employeesResponse.data.success) {
        setEmployees(employeesResponse.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = () => {
    setSelectedDepartment(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleEditDepartment = (department) => {
    setSelectedDepartment(department);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewDepartment = (department) => {
    setSelectedDepartment(department);
    setModalMode('view');
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedDepartment(null);
  };

  const handleSave = async (formData) => {
    try {
      if (modalMode === 'create') {
        await departmentAPI.create(formData);
      } else if (modalMode === 'edit') {
        await departmentAPI.update(selectedDepartment.id, formData);
      }
      await loadData();
      handleModalClose();
    } catch (err) {
      throw new Error(err.response?.data?.message || 'Error saving department');
    }
  };

  // Filter departments by selected company
  const filteredDepartments = departments.filter(dept => 
    !selectedCompany || dept.company_id === selectedCompany
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Department Management</h1>
          <p className="text-gray-600">Manage departments and organizational structure</p>
        </div>
        <div className="flex items-center space-x-4">
          {companies.length > 1 && (
            <select
              value={selectedCompany}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Companies</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={handleCreateDepartment}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Department
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {filteredDepartments.map((department) => {
          const company = companies.find(c => c.id === department.company_id);
          const manager = employees.find(e => e.id === department.manager_id);
          const departmentEmployees = employees.filter(e => e.department_id === department.id);
          // Use API-provided employee_count, fallback to filtered count
          const employeeCount = department.employee_count || departmentEmployees.length;

          return (
            <div key={department.id} className="bg-white overflow-hidden shadow rounded-lg border border-gray-200">
              <div className="p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <BuildingOffice2Icon className="h-10 w-10 text-primary-600" />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{department.name}</h3>
                    <p className="text-sm text-gray-500">Code: {department.code || 'N/A'}</p>
                    {company && <p className="text-sm text-gray-500">{company.name}</p>}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="flex items-center justify-center">
                        <UsersIcon className="h-5 w-5 text-gray-400 mr-1" />
                        <span className="text-2xl font-bold text-gray-900">{employeeCount}</span>
                      </div>
                      <p className="text-sm text-gray-500">Employees</p>
                    </div>
                    <div>
                      <div className="flex items-center justify-center">
                        <span className={`text-2xl font-bold ${department.is_active ? 'text-green-600' : 'text-red-600'}`}>
                          {department.is_active ? '●' : '○'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {department.is_active ? 'Active' : 'Inactive'}
                      </p>
                    </div>
                  </div>
                </div>

                {manager && (
                  <div className="mt-4 flex items-center text-sm text-gray-600">
                    <UserIcon className="h-4 w-4 mr-1" />
                    Manager: {manager.first_name} {manager.last_name}
                  </div>
                )}

                {department.description && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 line-clamp-2">{department.description}</p>
                  </div>
                )}

                {department.location && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">📍 {department.location}</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-3 flex justify-end space-x-2">
                <button
                  onClick={() => handleViewDepartment(department)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View
                </button>
                <button
                  onClick={() => handleEditDepartment(department)}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Edit
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredDepartments.length === 0 && (
        <div className="text-center py-12">
          <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No departments</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new department.</p>
          <div className="mt-6">
            <button
              onClick={handleCreateDepartment}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Department
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <DepartmentModal
          mode={modalMode}
          department={selectedDepartment}
          companies={companies}
          employees={employees}
          onClose={handleModalClose}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const DepartmentModal = ({ mode, department, companies, employees, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    company_id: companies.length > 0 ? companies[0].id : '',
    manager_id: '',
    parent_department_id: '',
    location: '',
    cost_center: '',
    budget_amount: '',
    budget_year: new Date().getFullYear(),
    phone: '',
    email: '',
    department_type: '',
    business_unit: '',
    is_active: true,
    ...department
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'view') return;

    try {
      setLoading(true);
      setError('');
      await onSave(formData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const title = mode === 'create' ? 'Add New Department' : mode === 'edit' ? 'Edit Department' : 'Department Details';

  // Filter employees for manager selection (only active employees)
  const managerOptions = employees.filter(emp => emp.employment_status === 'active');

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Basic Information</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Department Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Department Code</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company *</label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleChange}
                  required
                  disabled={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Department Manager</label>
                <select
                  name="manager_id"
                  value={formData.manager_id}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Manager</option>
                  {managerOptions.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.first_name} {employee.last_name} - {employee.job_title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Department Type</label>
                <select
                  name="department_type"
                  value={formData.department_type}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select Type</option>
                  <option value="Operations">Operations</option>
                  <option value="Support">Support</option>
                  <option value="Revenue">Revenue</option>
                  <option value="Administrative">Administrative</option>
                  <option value="Strategic">Strategic</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Business Unit</label>
                <input
                  type="text"
                  name="business_unit"
                  value={formData.business_unit}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Contact & Location */}
            <div className="space-y-4">
              <h4 className="text-md font-medium text-gray-900 border-b pb-2">Contact & Location</h4>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Cost Center</label>
                <input
                  type="text"
                  name="cost_center"
                  value={formData.cost_center}
                  onChange={handleChange}
                  readOnly={isReadOnly}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Budget Amount</label>
                  <input
                    type="number"
                    name="budget_amount"
                    value={formData.budget_amount}
                    onChange={handleChange}
                    step="0.01"
                    readOnly={isReadOnly}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Budget Year</label>
                  <input
                    type="number"
                    name="budget_year"
                    value={formData.budget_year}
                    onChange={handleChange}
                    readOnly={isReadOnly}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              readOnly={isReadOnly}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              disabled={isReadOnly}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
              Department is active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <button
                type="submit"
                disabled={loading}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Create Department' : 'Update Department'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default DepartmentManagement;